import { useState, useMemo } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  Typography,
  useTheme
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { hexToRgba } from "@aclymatepackages/converters";
import { truncateText } from "@aclymatepackages/other-helpers";

import SlideLayout from "../../../../../layouts/SlideLayout";

import OpenAiVendorCategorization from "../../../../../inputs/vendors/OpenAiVendorCategorization";
import {
  confidenceLabel,
  matchBasisLabel,
  calculateMergeDelta,
  sumMergeDeltas,
  formatEmissionsDelta
} from "./helpers";

const ClusterMemberRow = ({
  vendor,
  isPrimary,
  deltaDisplay,
  onSelectPrimary,
  onRemove
}) => {
  const { palette } = useTheme();
  const { id, name, naicsTitle } = vendor;

  const rowBackground = isPrimary
    ? hexToRgba(palette.primary.main, 0.08)
    : "transparent";
  const rowBorderColor = isPrimary
    ? palette.primary.main
    : palette.backgroundGray.dark;

  return (
    <Paper
      variant="outlined"
      style={{ backgroundColor: rowBackground, borderColor: rowBorderColor }}
    >
      <Box p={2}>
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Radio
              checked={isPrimary}
              onChange={() => onSelectPrimary(id)}
              inputProps={{ "aria-label": `Select ${name} as primary vendor` }}
            />
          </Grid>
          <Grid item xs>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle1">{name}</Typography>
              </Grid>
              {isPrimary && (
                <Grid item>
                  <Chip
                    size="small"
                    color="primary"
                    label="Primary"
                    variant="filled"
                  />
                </Grid>
              )}
            </Grid>
            <Typography variant="caption" color="textSecondary">
              {`Industry: ${naicsTitle}`}
            </Typography>
            {deltaDisplay && (
              <Typography
                variant="caption"
                color={deltaDisplay.color}
                display="block"
              >
                {`${deltaDisplay.label} after merge`}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => onRemove(id)}
            >
              Not a duplicate
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const NaicsOverrideSection = ({
  override,
  setOverride,
  keptVendors,
  primaryVendor
}) => {
  const overrideImpactDisplay = useMemo(() => {
    if (!override || !primaryVendor || !keptVendors || !keptVendors.length) {
      return null;
    }
    const overrideTarget = { tonsCo2ePerDollar: override.tonsCo2ePerDollar };
    const withOverride = sumMergeDeltas(
      keptVendors.map((vendor) => calculateMergeDelta(vendor, overrideTarget))
    );
    const withoutOverride = sumMergeDeltas(
      keptVendors
        .filter((vendor) => vendor.id !== primaryVendor.id)
        .map((vendor) => calculateMergeDelta(vendor, primaryVendor))
    );
    return formatEmissionsDelta({
      delta: withOverride.delta - withoutOverride.delta,
      isKnown: withOverride.isKnown && withoutOverride.isKnown
    });
  }, [override, primaryVendor, keptVendors]);

  const editVendor = (field) => (value) =>
    setOverride((current) => ({
      ...(current || {}),
      [field]: value
    }));

  return (
    <Box mt={3}>
      <Typography variant="subtitle2" gutterBottom>
        Change the industry first
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Describe what this vendor does and we&apos;ll suggest matching industries.
        Pick a suggestion to use it instead of the primary&apos;s existing industry
        on the merge.
      </Typography>
      <Grid container direction="column" spacing={2}>
        <OpenAiVendorCategorization
          naicsCode={override?.naicsCode || null}
          naicsTitle={override?.naicsTitle || null}
          editVendor={editVendor}
          questionText="Describe what this vendor does"
        />
      </Grid>
      {override && override.naicsTitle && (
        <Box mt={2}>
          <Typography variant="caption" color="primary" display="block">
            {`Will merge using industry: ${truncateText(override.naicsTitle, 60)}`}
          </Typography>
          {overrideImpactDisplay && (
            <Typography
              variant="caption"
              color={overrideImpactDisplay.color}
              display="block"
            >
              {`Override impact: ${overrideImpactDisplay.label.toLowerCase()} vs current industry`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const DuplicateClusterSlider = ({
  cluster,
  onClose,
  onMerge,
  onDismiss
}) => {
  const { vendors, confidence, matchBasis, recommendedPrimaryId, industriesDiffer } = cluster;
  const { palette } = useTheme();

  const [primaryId, setPrimaryId] = useState(
    recommendedPrimaryId || vendors[0].id
  );
  const [removedIds, setRemovedIds] = useState([]);
  const [naicsOverride, setNaicsOverride] = useState(null);
  const [acknowledgedIndustryDifference, setAcknowledgedIndustryDifference] = useState(false);

  const keptVendors = vendors.filter(({ id }) => !removedIds.includes(id));
  const removedVendors = vendors.filter(({ id }) => removedIds.includes(id));
  const primaryVendor = keptVendors.find(({ id }) => id === primaryId);
  const secondaryIds = keptVendors
    .map(({ id }) => id)
    .filter((id) => id !== primaryId);
  const canMerge =
    keptVendors.length >= 2 &&
    !!primaryVendor &&
    (!industriesDiffer || acknowledgedIndustryDifference);

  const netImpact = primaryVendor
    ? sumMergeDeltas(
        keptVendors
          .filter((vendor) => vendor.id !== primaryId)
          .map((vendor) => calculateMergeDelta(vendor, primaryVendor))
      )
    : { delta: 0, isKnown: true };
  const netImpactDisplay = formatEmissionsDelta(netImpact);

  const onSelectPrimary = (id) => setPrimaryId(id);

  const onRemove = (id) => {
    const nextRemoved = [...removedIds, id];
    setRemovedIds(nextRemoved);
    const stillKept = vendors.filter(
      ({ id: vendorId }) => !nextRemoved.includes(vendorId)
    );
    if (id === primaryId && stillKept.length) {
      setPrimaryId(stillKept[0].id);
    }
  };

  const onUndoRemovals = () => setRemovedIds([]);

  const toPairMember = (vendor) => ({
    vendorId: vendor.id,
    relationshipId: vendor.relationshipId
  });

  const buildRejectedPairs = () =>
    removedIds.flatMap((removedId) => {
      const removedVendor = vendors.find(({ id }) => id === removedId);
      return vendors
        .filter(({ id }) => !removedIds.includes(id))
        .map((keptVendor) => ({
          a: toPairMember(removedVendor),
          b: toPairMember(keptVendor)
        }));
    });

  const onConfirmMerge = () => {
    onMerge({
      clusterId: cluster.clusterId,
      primaryId,
      keptIds: keptVendors.map(({ id }) => id),
      rejectedPairs: buildRejectedPairs(),
      naicsOverride,
      ...(industriesDiffer ? { acknowledgedIndustryDifference } : {})
    });
    onClose();
  };

  const onConfirmDismiss = () => {
    onDismiss(cluster.clusterId, { rejectedPairs: buildRejectedPairs() });
    onClose();
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Box
              p={1.5}
              style={{
                backgroundColor: hexToRgba(palette.primary.main, 0.15),
                borderRadius: 999,
                display: "flex"
              }}
            >
              <StorefrontIcon color="primary" />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5">Review duplicate cluster</Typography>
            <Typography variant="caption" color="textSecondary">
              {`${vendors.length} vendors flagged · ${
                matchBasisLabel(matchBasis) || `${confidenceLabel(confidence)} confidence`
              } (${Math.round(confidence * 100)}%)`}
            </Typography>
          </Grid>
        </Grid>
      }
      content={
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              Pick the vendor record to keep — the others will merge into it.
              Use &quot;Not a duplicate&quot; to remove a vendor from this cluster; we&apos;ll
              also stop flagging that pair in the future.
            </Typography>
          </Grid>
          {keptVendors.length < 2 && (
            <Grid item>
              <Paper
                variant="outlined"
                style={{
                  borderColor: palette.warning.main,
                  backgroundColor: hexToRgba(palette.warning.main, 0.08)
                }}
              >
                <Box p={2}>
                  <Typography variant="body2">
                    Fewer than two vendors remain. Undo a removal below, or
                    dismiss the cluster.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}
          {keptVendors.map((vendor) => {
            const isPrimaryVendor = vendor.id === primaryId;
            const deltaDisplay = isPrimaryVendor
              ? null
              : formatEmissionsDelta(
                  calculateMergeDelta(vendor, primaryVendor)
                );
            return (
              <Grid item key={`cluster-member-${vendor.id}`}>
                <ClusterMemberRow
                  vendor={vendor}
                  isPrimary={isPrimaryVendor}
                  deltaDisplay={deltaDisplay}
                  onSelectPrimary={onSelectPrimary}
                  onRemove={onRemove}
                />
              </Grid>
            );
          })}
          {!!removedVendors.length && (
            <Grid item>
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onUndoRemovals}
                  >
                    Undo
                  </Button>
                }
              >
                {`Removed ${removedVendors.length} from this cluster: ${removedVendors
                  .map(({ name }) => name)
                  .join(", ")}`}
              </Alert>
            </Grid>
          )}
          {industriesDiffer && keptVendors.length >= 2 && (
            <Grid item>
              <Paper
                variant="outlined"
                style={{
                  borderColor: palette.warning.main,
                  backgroundColor: hexToRgba(palette.warning.main, 0.08)
                }}
              >
                <Box p={2}>
                  <Typography variant="body2" gutterBottom>
                    These vendors are classified in different industries.
                    Merging will apply the kept vendor&apos;s industry to all of
                    them — confirm this is still the same real-world vendor
                    before continuing.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acknowledgedIndustryDifference}
                        onChange={(e) =>
                          setAcknowledgedIndustryDifference(e.target.checked)
                        }
                      />
                    }
                    label="I've confirmed these are the same vendor"
                  />
                </Box>
              </Paper>
            </Grid>
          )}
          {canMerge && (
            <Grid item>
              <Typography
                variant="subtitle2"
                color={netImpactDisplay.color}
                align="right"
              >
                {`Net merge impact: ${netImpactDisplay.label.toLowerCase()}`}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Paper
              variant="outlined"
              style={{
                backgroundColor: hexToRgba(palette.primary.main, 0.05),
                borderColor: hexToRgba(palette.primary.main, 0.3)
              }}
            >
              <Box p={2}>
                <Typography variant="body2">
                  The kept vendor&apos;s industry is what survives the merge. If it
                  looks wrong, override it below — the new industry will
                  replace the primary&apos;s existing one as part of this merge.
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item>
            <NaicsOverrideSection
              override={naicsOverride}
              setOverride={setNaicsOverride}
              keptVendors={keptVendors}
              primaryVendor={primaryVendor}
            />
          </Grid>
        </Grid>
      }
      footer={
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <Button onClick={onConfirmDismiss} color="inherit">
              Not duplicates — dismiss
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!canMerge}
              onClick={onConfirmMerge}
            >
              {`Merge ${secondaryIds.length} vendor${
                secondaryIds.length === 1 ? "" : "s"
              } into "${primaryVendor ? primaryVendor.name : ""}"`}
            </Button>
          </Grid>
        </Grid>
      }
    />
  );
};

export default DuplicateClusterSlider;
